type UnknownEvent = Record<string, unknown>;
type UnknownHint = { originalException?: unknown } & Record<string, unknown>;

export type BeforeSendFn = (
  event: UnknownEvent,
  hint: UnknownHint,
) => UnknownEvent | null | PromiseLike<UnknownEvent | null>;

export type SharedSentryInput = {
  app: unknown;
  dsn: string | undefined;
  release?: string;
  dist?: string;
  environment?: string;
  sampleRate?: number;
  ignoreErrors?: (string | RegExp)[];
  beforeSend?: BeforeSendFn;
};

const SAFELINK_BOT_PATTERN =
  /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;

export const SHARED_IGNORE_ERRORS: (string | RegExp)[] = [
  /CanceledError/,
  "ERR_CANCELED",
  // Browser autoplay / permission denials — never an app bug, always env/user.
  /NotAllowedError/,
  // Universally-known benign DOM noise.
  /ResizeObserver loop/,
  /ResizeObserver.*parameter 1 is not of type 'Element'/,
  // Clipboard write while document is not focused — benign focus race.
  /Document is not focused/,
  // Stale-deploy class: chunks/CSS removed by a fresh deploy while a tab is open.
  /Failed to fetch dynamically imported module/,
  /Unable to preload CSS/,
  // Office 365 / Outlook Safelink URL-preview crawler echo.
  SAFELINK_BOT_PATTERN,
];

export const isCanceledRejection = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const e = error as Record<string, unknown>;
  return (
    e.canceled === true ||
    e.code === "ERR_CANCELED" ||
    e.name === "CanceledError"
  );
};

export const isNetworkOrTimeoutError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const e = error as Record<string, unknown>;
  return (
    e.code === "ERR_NETWORK" ||
    e.code === "ECONNABORTED" ||
    e.message === "Network Error"
  );
};

export const isBenignBrowserError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const e = error as { name?: unknown; message?: unknown };
  if (e.name === "NotAllowedError") {
    return true;
  }
  const message = typeof e.message === "string" ? e.message : "";
  if (message.includes("Document is not focused")) {
    return true;
  }
  if (!message.includes("ResizeObserver")) {
    return false;
  }
  return (
    message.includes("loop") ||
    message.includes("parameter 1 is not of type 'Element'")
  );
};

export const isStaleDeployError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  const message = (error as { message?: unknown }).message;
  if (typeof message !== "string") {
    return false;
  }
  return (
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Unable to preload CSS")
  );
};

// Outlook Safelink / Office 365 URL-preview crawler synthesizes this rejection
// when it fetches our URLs as part of email link expansion.
export const isThirdPartyBotEcho = (error: unknown): boolean => {
  const value =
    typeof error === "string"
      ? error
      : ((error as { message?: unknown })?.message as string | undefined);
  if (typeof value !== "string") {
    return false;
  }
  return SAFELINK_BOT_PATTERN.test(value);
};

// Sentinel for errors that are thrown for control flow but already handled
// inline (e.g. vee-validate rethrows that already populated form errors).
export const isExpectedFormError = (error: unknown): boolean => {
  if (!error || typeof error !== "object") {
    return false;
  }
  return (
    (error as { isExpectedFormError?: unknown }).isExpectedFormError === true
  );
};

export const buildSentryConfig = ({
  app,
  dsn,
  release,
  dist,
  environment,
  sampleRate,
  ignoreErrors,
  beforeSend,
}: SharedSentryInput): Record<string, unknown> => {
  const mergedBeforeSend: BeforeSendFn = (event, hint) => {
    const originalException = hint?.originalException;
    if (
      isCanceledRejection(originalException) ||
      isNetworkOrTimeoutError(originalException) ||
      isBenignBrowserError(originalException) ||
      isStaleDeployError(originalException) ||
      isThirdPartyBotEcho(originalException) ||
      isExpectedFormError(originalException)
    ) {
      return null;
    }
    return beforeSend ? beforeSend(event, hint) : event;
  };

  return {
    app,
    dsn,
    release,
    dist,
    environment,
    sampleRate,
    // Performance traces are disabled org-wide: tracing ships a span for every
    // transaction and burns through our Sentry event quota fast for near-zero
    // diagnostic value at our current scale. Flip per app if you genuinely
    // need performance data, and pair it with a realistic sample rate.
    tracesSampleRate: 0,
    maxBreadcrumbs: 50,
    ignoreErrors: [...SHARED_IGNORE_ERRORS, ...(ignoreErrors ?? [])],
    beforeSend: mergedBeforeSend,
  };
};
